<template>
  <div>
    <h1>Adresse du cabinet</h1>
    <form @submit.prevent="handleSubmit">
      <div class="row">
        <div class="col _7">
          <FormInput
            id="register-office-address"
            v-model="form.practiceAddress"
            label="Numéro et nom de la rue"
            required
            @blur="$v.form.practiceAddress.$touch()"
          >
            <template v-if="$v.form.practiceAddress.$dirty" #error>
              <template v-if="!$v.form.practiceAddress.required">L'adresse est obligatoire</template>
            </template>
          </FormInput>
        </div>
        <div class="col _5">
          <FormInput
            id="register-office-postal-code"
            v-model="form.practicePostalCode"
            label="Code postal"
            required
            @blur="$v.form.practicePostalCode.$touch()"
          >
            <template v-if="$v.form.practicePostalCode.$dirty" #error>
              <template v-if="!$v.form.practicePostalCode.required">Le code postal est obligatoire</template>
              <template v-else-if="$v.form.practicePostalCode.$invalid">Le code postal est invalide</template>
            </template>
          </FormInput>
        </div>
      </div>
      <div class="row">
        <div class="col _7">
          <FormInput
            id="register-office-city"
            v-model="form.practiceCity"
            label="Ville"
            required
            @blur="$v.form.practiceCity.$touch()"
          >
            <template v-if="$v.form.practiceCity.$dirty" #error>
              <template v-if="!$v.form.practiceCity.required">La ville est obligatoire</template>
            </template>
          </FormInput>
        </div>
        <div class="col _5">
          <FormCountrySelect
            id="register-office-country"
            v-model="form.practiceCountry"
            label="Pays"
            required
            @change="$v.form.practiceCountry.$touch()"
          >
            <template v-if="$v.form.practiceCountry.$dirty" #error>
              <template v-if="!$v.form.practiceCountry.required">Le pays est obligatoire</template>
            </template>
          </FormCountrySelect>
        </div>
      </div>

      <HorizontalDivider color="#DDD">
        <FormCheckbox v-model="form.usePracticeAddress" class="same-address">
          Adresse de facturation identique
        </FormCheckbox>
      </HorizontalDivider>

      <div v-if="!form.usePracticeAddress">
        <div class="row">
          <div class="col _7">
            <FormInput
              id="register-billing-address"
              v-model="form.companyAddress"
              label="Numéro et nom de la rue"
              required
              @blur="$v.form.companyAddress.$touch()"
            >
              <template v-if="$v.form.companyAddress.$dirty" #error>
                <template v-if="!$v.form.companyAddress.required">L'adresse est obligatoire</template>
              </template>
            </FormInput>
          </div>
          <div class="col _5">
            <FormInput
              id="register-billing-postal-code"
              v-model="form.companyPostalCode"
              label="Code postal"
              required
              @blur="$v.form.companyPostalCode.$touch()"
            >
              <template v-if="$v.form.companyPostalCode.$dirty" #error>
                <template v-if="!$v.form.companyPostalCode.required">Le code postal est obligatoire</template>
                <template v-else-if="$v.form.companyPostalCode.$invalid">Le code postal est invalide</template>
              </template>
            </FormInput>
          </div>
        </div>
        <div class="row">
          <div class="col _7">
            <FormInput
              id="register-billing-city"
              v-model="form.companyCity"
              label="Ville"
              required
              @blur="$v.form.companyCity.$touch()"
            >
              <template v-if="$v.form.companyCity.$dirty" #error>
                <template v-if="!$v.form.companyCity.required">La ville est obligatoire</template>
              </template>
            </FormInput>
          </div>
          <div class="col _5">
            <FormCountrySelect
              id="register-billing-country"
              v-model="form.companyCountry"
              label="Pays"
              required
              @change="$v.form.companyCountry.$touch()"
            >
              <template v-if="$v.form.companyCountry.$dirty" #error>
                <template v-if="!$v.form.companyCountry.required">Le pays est obligatoire</template>
              </template>
            </FormCountrySelect>
          </div>
        </div>
      </div>

      <SignupButtons :can-be-submitted="!$v.$invalid" />
    </form>
  </div>
</template>

<script>
  import ButtonGreen from '@/components/ButtonGreen';
  import SignupButtons from '@/components/Signup/SignupButtons';
  import FormCountrySelect from '@/components/Form/FormCountrySelect';
  import FormInput from '@/components/Form/FormInput';
  import HorizontalDivider from '@/components/HorizontalDivider';
  import FormCheckbox from '@/components/Form/FormCheckbox';
  import { and, integer, maxLength, minLength, required, requiredUnless } from '@byscripts/vuelidate/lib/validators';
  import { country } from '@/helpers/validation';

  export default {
    name: 'SignupPractice',
    components: { FormCheckbox, HorizontalDivider, FormInput, FormCountrySelect, SignupButtons, ButtonGreen },
    data() {
      return {
        form: {
          practiceAddress: this.$store.state.signup.form.practiceAddress,
          practicePostalCode: this.$store.state.signup.form.practicePostalCode,
          practiceCity: this.$store.state.signup.form.practiceCity,
          practiceCountry: this.$store.state.signup.form.companyCountry || 'FR',// PracticeCountry is a same like companyCountry
          usePracticeAddress: this.$store.state.signup.form.usePracticeAddress === undefined
            ? true
            : this.$store.state.signup.form.usePracticeAddress,
          companyAddress: this.$store.state.signup.form.companyAddress,
          companyPostalCode: this.$store.state.signup.form.companyPostalCode,
          companyCity: this.$store.state.signup.form.companyCity,
          companyCountry: this.$store.state.signup.form.companyCountry || 'FR',
        },
      };
    },
    validations: {
      form: {
        practiceAddress: { required },
        practicePostalCode: {
          required,
          length: maxLength(20),
        },
        practiceCity: { required },
        practiceCountry: {
          required,
          country,
        },
        usePracticeAddress: {},
        companyAddress: { required: requiredUnless('usePracticeAddress') },
        companyPostalCode: {
          required: requiredUnless('usePracticeAddress'),
          length: maxLength(20),
        },
        companyCity: { required: requiredUnless('usePracticeAddress') },
        companyCountry: {
          required: requiredUnless('usePracticeAddress'),
          country,
        },
      },
    },
    methods: {
      handleSubmit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          return this.$addError('Veuillez vérifier le formulaire');
        }

        this.$store.dispatch('signup/saveForm', { form: this.form });
        this.$store.dispatch('signup/goToNextStep');
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../assets/grid";

  .form-input {
    margin-bottom: 3rem;
  }

  .horizontal-divider {
    margin: 2rem 0;
  }
  ::v-deep .signup-buttons.has-back-button{
    @include onMobile{
      column-gap: 3.7rem;
      justify-content: end;
      flex-wrap: wrap;
    }
  }
</style>
